import React from 'react';
import { Mail, Phone, MapPin, Instagram, MessageCircle, ShieldCheck } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/context/LanguageContext';
import { t, tx } from '@/translations/translations';

const WHATSAPP_NUMBER = '5543233705';
const INSTAGRAM_HANDLE = 'mitzva_fit';
const CONTACT_EMAIL = 'mitzvafit@gmail.com';
const WHATSAPP_URL = `https://wa.me/52${WHATSAPP_NUMBER}?text=%C2%A1Hola%21%20Tengo%20una%20duda%20sobre%20los%20tzitziot%20de%20Mitzva%20Fit.`;

const Footer = () => {
  const { language } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand */}
          <div>
            <img
              src="/products/mitzva-logo-white.webp"
              alt="Mitzva Fit"
              className="h-14 w-auto mb-4"
            />
            <p className="text-sm leading-relaxed mb-4">
              {t(language, 'footer.description')}
            </p>
            <div className="flex items-center gap-2">
              <a
                href={`https://instagram.com/${INSTAGRAM_HANDLE}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-instagram"
                className="inline-flex items-center gap-1.5 bg-slate-800 hover:bg-gradient-to-br hover:from-purple-500 hover:via-pink-500 hover:to-orange-400 hover:text-white text-slate-300 text-xs font-medium px-3 py-2 rounded-lg transition-all"
              >
                <Instagram className="h-4 w-4" />
                @{INSTAGRAM_HANDLE}
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="inline-flex items-center gap-1.5 bg-[#25D366]/20 hover:bg-[#25D366] text-[#25D366] hover:text-white text-xs font-medium px-3 py-2 rounded-lg transition-all"
              >
                <MessageCircle className="h-4 w-4" />
                WhatsApp
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t(language, 'footer.quickLinks')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#productos" className="hover:text-cyan-400 transition-colors">
                  {t(language, 'nav.products')}
                </a>
              </li>
              <li>
                <a href="#sobre-nosotros" className="hover:text-cyan-400 transition-colors">
                  {tx(language, 'Nuestra Misión', 'Our Mission', 'המשימה שלנו')}
                </a>
              </li>
              <li>
                <a href="#mas-del-tzitzit" className="hover:text-cyan-400 transition-colors">
                  {tx(language, 'Más del Tzitzit', 'More about Tzitzit', 'עוד על הציצית')}
                </a>
              </li>
              <li>
                <a href="#faq" className="hover:text-cyan-400 transition-colors">
                  {tx(language, 'Preguntas Frecuentes', 'FAQ', 'שאלות נפוצות')}
                </a>
              </li>
              <li>
                <a href="#contacto" className="hover:text-cyan-400 transition-colors">
                  {t(language, 'nav.contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Payments & Shipping */}
          <div>
            <h4 className="text-white font-semibold mb-4">
              {tx(language, 'Pagos y Envíos', 'Payments & Shipping', 'תשלומים ומשלוחים')}
            </h4>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-green-400" />
                <span>
                  {tx(language, 'Pago 100% seguro', '100% secure payment', 'תשלום מאובטח 100%')}
                </span>
              </li>
              <li className="text-slate-400 text-xs">
                {tx(language, 'Mercado Pago · PayPal · Transferencia', 'Mercado Pago · PayPal · Bank transfer', 'Mercado Pago · PayPal · העברה בנקאית')}
              </li>
              <li className="pt-2 text-sm">
                {tx(language, 'Envío a todo México', 'Nationwide shipping in Mexico', 'משלוח לכל רחבי מקסיקו')}
              </li>
              <li className="text-slate-400 text-xs">
                {tx(language, '5+ productos = envío gratis', '5+ products = free shipping', '5+ מוצרים = משלוח חינם')}
              </li>
              <li className="pt-2 text-sm">
                {tx(language, 'Certificación', 'Certification', 'כשרות')}:
              </li>
              <li className="text-slate-400 text-xs">
                {t(language, 'footer.kashrutCert')}
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t(language, 'contact.title')}</h4>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 w-full bg-[#25D366] hover:bg-[#1ebe57] text-white font-semibold py-2.5 rounded-lg transition-colors"
                  data-testid="footer-whatsapp-cta"
                >
                  <MessageCircle className="h-4 w-4" />
                  {tx(language, 'Chatea con nosotros', 'Chat with us', 'דברו איתנו')}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-cyan-400 transition-colors break-all">
                  {CONTACT_EMAIL}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <div>
                  <p>+52 55 4323 3705</p>
                  <p className="text-xs text-slate-400">{t(language, 'footer.phoneHours')}</p>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0 text-cyan-400" />
                <p>{tx(language, 'Ciudad de México, México', 'Mexico City, Mexico', 'מקסיקו סיטי, מקסיקו')}</p>
              </li>
            </ul>
          </div>
        </div>

        <Separator className="my-8 bg-slate-800" />

        <div className="flex flex-col md:flex-row justify-between items-center text-xs text-slate-400 gap-3">
          <p>© {currentYear} Mitzva Fit. {t(language, 'footer.rights')}</p>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openLegal', { detail: 'privacy' }))}
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-privacy-link"
            >
              {t(language, 'footer.privacy')}
            </button>
            <span className="text-slate-700">·</span>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openLegal', { detail: 'terms' }))}
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-terms-link"
            >
              {t(language, 'footer.terms')}
            </button>
            <span className="text-slate-700">·</span>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openOrderLookup'))}
              className="hover:text-cyan-400 transition-colors"
              data-testid="footer-order-lookup"
            >
              {tx(language, 'Mi Pedido', 'My Order', 'ההזמנה שלי')}
            </button>
          </div>
          <p>
            {t(language, 'footer.madeWith')} <span className="text-red-400">❤</span> {t(language, 'footer.forCommunity')}
          </p>
        </div>
        <div className="text-center mt-4">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('openAdmin'))}
            className="text-[10px] text-slate-700 hover:text-cyan-400 transition-colors"
            data-testid="admin-link"
          >
            Admin
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
